import { Injectable, inject } from '@angular/core'
import { DateRange } from '@angular/material/datepicker'
import {
  Property,
  PropertyType,
  SortEnumType,
  AssetView,
  AssetSortInput,
  AssetRowSortInput,
  GetAssetViewsGQL,
  CreateAssetViewGQL,
  UpdateAssetViewGQL,
  DeleteAssetViewGQL,
  PageInfo,
  GetAssetRowsPrevPageGQL,
  AssetRowsConnection,
  AssetRow,
  PropertyValue,
  GetAssetRowsGQL,
  GetAssetRowsQuery,
} from '@ziptility/graphql'
import { Apollo, gql } from 'apollo-angular'
import { ComponentStore } from '@ngrx/component-store'
import { first, isEmpty, pullAt, without, compact, groupBy, keys } from 'lodash/fp'
import { EMPTY, Observable, firstValueFrom } from 'rxjs'
import { catchError, exhaustMap, map as rxMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import moment from 'moment'
import { AssetRowGraphqlQueryParser } from '@ziptility/tasks/query-parser/asset-row-query-parser'
import { BvAlertService } from '@ziptility/bv-alert'
import { ActivatedRoute, Router } from '@angular/router'
import { isArray } from 'lodash'
import { MatDialog } from '@angular/material/dialog'
import {
  BvViewFilter,
  BvViewFilterOption,
  FilterOptionsMap,
  AssetTableData,
  AssetTableRow,
  ColumnRef,
} from '@ziptility/tasks'
import { DatePickerModal } from '@ziptility/views'
import { toSignal } from '@angular/core/rxjs-interop'
import { ViewPropertyCategory } from '@ziptility/graphql'
import { ApolloQueryResult } from '@apollo/client/core'

const GetAssetPropertyFilterValues = gql`
  query GetAssetPropertyFilterValues($name: String!) {
    assetPropertyFilterValues(propertyName: $name)
  }
`

const GetAssetPropertyFilterNameOptions = gql`
  query GetAssetFilterNames {
    assetPropertyFilterNames {
      name
      type
      source
    }
  }
`

interface AssetViewStoreState {
  views: AssetView[]
  properties: Property[]
  selectedView: AssetView | undefined
  selectedFilters: BvViewFilter[]
  filterOptionMap: FilterOptionsMap
  selectedViewId: string | undefined
  selectedFilter: string | undefined
  loadingFilterOptions: boolean
  order: AssetSortInput[]
  select: string[]
  defaultFilters: BvViewFilter[]
  tableRows: AssetTableRow[]
  pageInfo: PageInfo | undefined
  totalCount: number | undefined
  columns: ColumnRef[]
  pageSize: number
  searching: boolean
  initialized: boolean
}

@Injectable()
export class AssetViewStore extends ComponentStore<AssetViewStoreState> {
  readonly apollo = inject(Apollo)
  readonly router = inject(Router)
  readonly route = inject(ActivatedRoute)
  readonly matDialog = inject(MatDialog)
  readonly getAssetViews = inject(GetAssetViewsGQL)
  readonly createAssetView = inject(CreateAssetViewGQL)
  readonly updateAssetView = inject(UpdateAssetViewGQL)
  readonly deleteAssetView = inject(DeleteAssetViewGQL)
  readonly alertService = inject(BvAlertService)
  readonly getAssetRows = inject(GetAssetRowsGQL)
  readonly getAssetRowsPrevPage = inject(GetAssetRowsPrevPageGQL)

  readonly views$ = this.select((s) => s.views)
  readonly selectedViewId$ = this.select((s) => s.selectedViewId)
  readonly selectedView$ = this.select((s) => s.selectedView)
  readonly selectedFilter$ = this.select((s) => s.selectedFilter, { debounce: true })
  readonly selectedFilters$ = this.select((s) => s.selectedFilters)
  readonly filterOptions$ = this.select((s) => s.filterOptionMap)
  readonly hasChanges$ = this.select((s) => s.selectedFilters !== s.defaultFilters)
  readonly tableRows$ = this.select((s) => s.tableRows)
  readonly pageInfo$ = this.select((s) => s.pageInfo)
  readonly totalCount$ = this.select((s) => s.totalCount)
  readonly columns$ = this.select((s) => s.columns)
  readonly pageSize$ = this.select((s) => s.pageSize)
  readonly searching$ = this.select((s) => s.searching)
  readonly initialized$ = this.select((s) => s.initialized)

  readonly queryParser = new AssetRowGraphqlQueryParser()

  readonly where$ = this.select((s) => {
    return s.selectedFilters
  }).pipe(
    rxMap((filters) => {
      return this.queryParser.toGraphqlFilters(filters)
    }),
  )

  readonly where = toSignal(this.where$)

  readonly filters: string[][] = [
    ['Asset Type', 'More'],
    ['DateCreated', 'Creator', 'DateModified', 'Modifier'],
  ]

  constructor() {
    super({
      views: [],
      properties: [],
      selectedFilters: [],
      filterOptionMap: {},
      selectedView: undefined,
      selectedViewId: undefined,
      selectedFilter: undefined,
      loadingFilterOptions: false,
      order: [{ dateCreated: SortEnumType.Desc }],
      select: [],
      defaultFilters: [],
      tableRows: [],
      pageInfo: undefined,
      totalCount: undefined,
      columns: [],
      pageSize: 100,
      searching: false,
      initialized: false,
    })

  }

  /**
   * Observable that auto-fetches when filters change.
   * Component should subscribe to this with takeUntilDestroyed for proper cleanup.
   */
  readonly autoFetchOnFilterChange$ = this.where$.pipe(
    tap(() => {
      const { initialized, selectedFilters } = this.state()
      if (initialized && !isEmpty(selectedFilters)) {
        this.fetch()
      }
    }),
  )

  readonly updateColumnOrder = this.updater((state, columns: string[]) => {
    return {
      ...state,
      select: columns,
    }
  })

  readonly setTableData = this.updater(
    (state, data: { tableRows: AssetTableRow[]; pageInfo: PageInfo; totalCount: number; columns: ColumnRef[] }) => {
      return {
        ...state,
        tableRows: data.tableRows,
        pageInfo: data.pageInfo,
        totalCount: data.totalCount,
        columns: data.columns,
      }
    },
  )

  readonly clearTableData = this.updater((state) => {
    return {
      ...state,
      tableRows: [],
      pageInfo: undefined,
      totalCount: undefined,
      columns: [],
    }
  })

  readonly setPageSize = this.updater((state, pageSize: number) => {
    return {
      ...state,
      pageSize,
    }
  })

  readonly setSearching = this.updater((state, searching: boolean) => {
    return {
      ...state,
      searching,
    }
  })

  readonly setInitialized = this.updater((state, initialized: boolean) => {
    return {
      ...state,
      initialized,
    }
  })

  readonly selectFilter = this.updater((state: AssetViewStoreState, selectedFilter: string | undefined) => {
    return {
      ...state,
      selectedFilter,
    }
  })

  readonly toggleFilterOption = this.updater(
    (
      state: AssetViewStoreState,
      { filter, checked, option }: { filter: string; checked: boolean; option: BvViewFilterOption },
    ) => {
      const filterOptions = {
        ...state.filterOptionMap,
      }
      filterOptions[filter] = filterOptions[filter]?.map((o) => {
        if (o === option) {
          return {
            ...o,
            selected: checked,
          }
        } else {
          return {
            ...o,
            selected: option.type == PropertyType.DateTimeType ? false : o.selected,
          }
        }
      })

      return {
        ...state,
        filterOptionMap: filterOptions,
      }
    },
  )

  /**
   * Helper method to get the current where clause from filters
   */
  private getWhereClause() {
    return this.queryParser.toGraphqlFilters(this.state().selectedFilters) ?? {}
  }

  /**
   * Shared response handler for fetch operations
   * Processes the response and updates the store state
   */
  private handleFetchResponse(result: ApolloQueryResult<GetAssetRowsQuery>): void {
    const { data, error } = result
    this.setSearching(false)

    if (error) {
      console.error('Failed to fetch asset rows:', error)
      this.alertService.error('Failed to load assets. Please try again.')
      return
    }

    const { select } = this.state()
    const response = data.assetRows as AssetRowsConnection
    const { pageInfo, totalCount, nodes } = response
    const { rows, columns } = this.makeTableRows(nodes ?? [], select)

    this.setTableData({
      tableRows: rows,
      pageInfo: pageInfo,
      totalCount: totalCount,
      columns: columns,
    })
  }

  readonly fetch = this.effect(($: Observable<void>) => {
    return $.pipe(
      tap(() => this.setSearching(true)),
      switchMap(() => {
        const { order, pageSize } = this.state()
        return this.getAssetRows
          .fetch(
            {
              where: this.getWhereClause(),
              order: order,
              first: pageSize,
            },
            {
              fetchPolicy: 'network-only',
            },
          )
          .pipe(
            tap((result) => this.handleFetchResponse(result)),
            catchError((err) => {
              console.error('Apollo query failed in fetch:', err)
              this.setSearching(false)
              this.alertService.error('Failed to load assets. Please try again.')
              return EMPTY
            }),
          )
      }),
    )
  })

  readonly nextPage = this.effect(($: Observable<void>) => {
    return $.pipe(
      tap(() => this.setSearching(true)),
      switchMap(() => {
        const { order, pageSize, pageInfo } = this.state()

        if (!pageInfo?.endCursor) {
          console.warn('Cannot navigate to next page: no endCursor available')
          this.setSearching(false)
          return EMPTY
        }

        return this.getAssetRows
          .fetch(
            {
              where: this.getWhereClause(),
              order: order,
              first: pageSize,
              after: pageInfo.endCursor,
            },
            {
              fetchPolicy: 'network-only',
            },
          )
          .pipe(
            tap((result) => this.handleFetchResponse(result)),
            catchError((err) => {
              console.error('Apollo query failed in nextPage:', err)
              this.setSearching(false)
              this.alertService.error('Failed to load next page. Please try again.')
              return EMPTY
            }),
          )
      }),
    )
  })

  readonly previousPage = this.effect(($: Observable<void>) => {
    return $.pipe(
      tap(() => this.setSearching(true)),
      switchMap(() => {
        const { order, pageSize, pageInfo } = this.state()

        if (!pageInfo?.startCursor) {
          console.warn('Cannot navigate to previous page: no startCursor available')
          this.setSearching(false)
          return EMPTY
        }

        return this.getAssetRowsPrevPage
          .fetch({
            where: this.getWhereClause(),
            order,
            last: pageSize,
            before: pageInfo.startCursor,
          })
          .pipe(
            tap((result) => this.handleFetchResponse(result)),
            catchError((err) => {
              console.error('Apollo query failed in previousPage:', err)
              this.setSearching(false)
              this.alertService.error('Failed to load previous page. Please try again.')
              return EMPTY
            }),
          )
      }),
    )
  })

  /**
   * Initialize the store and trigger initial fetch if filters are ready
   */
  initialize(): void {
    this.setInitialized(true)
    const { selectedFilters, defaultFilters } = this.state()
    if (selectedFilters.length > 0 || !isEmpty(defaultFilters)) {
      this.fetch()
    }
  }

  readonly selectView = this.effect(($: Observable<{ id: string | undefined }>) => {
    return $.pipe(
      tap(({ id }) => {
        const { selectedView, selectedFilters: sf } = this.state()
        if (!id && !selectedView?.id && sf.length) {
          return
        }
        const view: AssetView | undefined = this.state().views.find((v) => v.id == id) ?? this.getDefaultViewForMap()
        const variables = view?.variables && !isEmpty(view.variables) ? JSON.parse(view.variables) : {}
        const where = variables.where ?? {}
        const select = variables.select ?? []
        const order = this.parseViewSorts(view)
        const selectedFilters = !isEmpty(where) ? this.queryParser.parse(where) : []

        this.patchState({
          selectedView: view,
          selectedFilters,
          order,
          select,
        })
        this.setFilterSelections(selectedFilters)
      }),
    )
  })

  readonly resetView = this.updater((state: AssetViewStoreState) => {
    return {
      ...state,
      selectedView: undefined,
      selectedFilters: this.get().defaultFilters,
      order: [{ dateCreated: SortEnumType.Desc }],
      select: [],
    }
  })

  readonly createView = this.effect(($: Observable<string>) => {
    return $.pipe(
      withLatestFrom(this.where$),
      switchMap(([name, where]) => {
        const { order, select } = this.state()
        return this.createAssetView
          .mutate({
            assetView: {
              name,
              variables: JSON.stringify({
                where,
                order,
                select,
              }),
            },
          })
          .pipe(
            rxMap((response) => {
              if (response.errors) {
                console.error('Failed to create asset view:', response.errors)
                this.alertService.error('Failed to create view. Please try again.')
                return
              }

              const view = response.data?.createAssetView.assetView as AssetView
              this.onSelectView(view)
              return view
            }),
            catchError((err) => {
              console.error('Failed to create asset view:', err)
              this.alertService.error('Failed to create view. Please try again.')
              return EMPTY
            }),
          )
      }),
      tap((view: AssetView | undefined) => {
        if (!view) {
          return
        }
        this.patchState({
          views: this.get().views.concat(view),
        })
      }),
    )
  })

  readonly updateView = this.effect(($: Observable<{ id: string; name: string }>) => {
    return $.pipe(
      withLatestFrom(this.where$),
      exhaustMap(([{ id, name }, where]) => {
        const { order, select } = this.state()
        return this.updateAssetView
          .mutate({
            assetView: {
              assetViewId: id,
              name,
              variables: JSON.stringify({
                where,
                order,
                select,
              }),
            },
          })
          .pipe(
            tap(() => {
              this.loadViews()
              this.alertService.success('Saved')
            }),
            catchError((err) => {
              console.error('Failed to update asset view:', err)
              this.alertService.error('Failed to save view. Please try again.')
              return EMPTY
            }),
          )
      }),
    )
  })

  readonly deleteView = this.effect(($: Observable<string>) => {
    return $.pipe(
      switchMap((id) => {
        return this.deleteAssetView
          .mutate({
            input: {
              assetViewId: id,
            },
          })
          .pipe(
            switchMap(() => this.getViews()),
            tap((views: AssetView[]) => {
              this.patchState({
                views,
              })
              const view = first(views)
              this.onSelectView(view)
              this.alertService.success('View deleted')
            }),
            catchError((err) => {
              console.error('Failed to delete asset view:', err)
              this.alertService.error('Failed to delete view. Please try again.')
              return EMPTY
            }),
          )
      }),
    )
  })

  readonly loadViews = this.effect(($: Observable<void>) => {
    return $.pipe(
      switchMap(() => this.getViews()),
      tap((views: AssetView[]) => {
        this.patchState({
          views,
        })
      }),
    )
  })

  async loadFilters(mapTitle: string): Promise<void> {
    this.patchState({
      defaultFilters: [new BvViewFilter('Team', PropertyType.StringType, [mapTitle], 'in', true)],
    })

    const [views, properties] = await Promise.all([this.getViews(), this.getFilterOptions()])
    const keys = ['Asset Type', 'Creator', 'Modifier']
    const filterOptions: FilterOptionsMap = {}
    const promises = keys.map((k) => {
      const prop = properties.find((p) => p.name === k)
      if (!prop) {
        return Promise.resolve([])
      }
      return this.getFilterValues(k)
    })

    const arr = await Promise.all(promises)

    keys.forEach((k) => {
      filterOptions[k] = arr[keys.indexOf(k)].map((v) => {
        return {
          title: v,
          value: v,
          type: properties.find((p) => p.name == k)?.type ?? PropertyType.StringType,
          selected: false,
        }
      })
    })

    const dates = ['DateCreated', 'DateModified']
    dates.forEach((d) => {
      filterOptions[d] = [
        {
          title: 'Last 7 Days',
          value: '-P7D',
          type: PropertyType.DateTimeType,
          selected: false,
        },

        {
          title: 'Last 14 Days',
          value: '-P14D',
          type: PropertyType.DateTimeType,
          selected: false,
        },
        {
          title: 'Last 30 Days',
          value: '-P30D',
          type: PropertyType.DateTimeType,
          selected: false,
        },

        {
          title: 'Custom',
          value: '',
          type: PropertyType.DateTimeType,
          selected: false,
        },
      ]
    })

    this.patchState({
      views,
      loadingFilterOptions: false,
      properties: properties,
      filterOptionMap: filterOptions,
    })
  }

  getFilterValues(name: string): Promise<string[]> {
    return firstValueFrom(
      this.apollo
        .query({
          query: GetAssetPropertyFilterValues,
          variables: {
            name,
          },
        })
        .pipe(rxMap((r: any) => r.data.assetPropertyFilterValues)),
    )
  }

  async getFilterOptions() {
    this.patchState({
      loadingFilterOptions: true,
    })

    const properties = await firstValueFrom(
      this.apollo
        .query({
          query: GetAssetPropertyFilterNameOptions,
        })
        .pipe(rxMap((r: any) => r.data.assetPropertyFilterNames as Property[])),
    )

    this.patchState({
      loadingFilterOptions: false,
    })
    return properties
  }

  getViews(): Promise<AssetView[]> {
    return firstValueFrom(
      this.getAssetViews
        .fetch(undefined, {
          fetchPolicy: 'network-only',
        })
        .pipe(rxMap((r) => r.data.assetViews as AssetView[])),
    )
  }

  isTopLevelFilter(name: string) {
    return name === 'Asset Type'
  }

  private defaultSort: AssetSortInput[] = [
    {
      dateCreated: SortEnumType.Desc,
    },
  ]

  parseViewSorts(view: AssetView): AssetRowSortInput[] {
    const value = view?.sorts && !isEmpty(view.sorts) ? JSON.parse(view.sorts) : this.defaultSort
    if (isArray(value)) {
      return value
    } else {
      return this.defaultSort
    }
  }

  private setFilterSelections(filters: BvViewFilter[]) {
    this.patchState((state) => {
      const filterOptions = {
        ...state.filterOptionMap,
      }
      Object.values(filterOptions).forEach((options) => options.forEach((o) => (o.selected = false)))

      filters.forEach((f) => {
        filterOptions[f.title]?.forEach((o) => (o.selected = f.value.includes(o.title)))
      })
      return {
        filterOptionMap: filterOptions,
      }
    })
  }

  getDefaultViewForMap() {
    const where = this.queryParser.toGraphqlFilters(this.state().defaultFilters)
    return {
      variables: JSON.stringify({
        where,
        order: [{ dateCreated: 'DESC' }],
        first: 50,
      }),
    } as AssetView
  }

  setFilters(filters: BvViewFilter[]) {
    this.patchState({
      selectedFilters: [...filters],
    })
  }

  updateDateFilter(index: number, range: DateRange<Date>, filterInfo?: BvViewFilter) {
    const value = range.start ? [range.start.toISOString()] : []
    if (range.end) {
      value.push(range.end.toISOString())
    }

    // Create a new array to ensure observable detects the change
    const filters = [...this.get().selectedFilters]
    const viewFilter = filters[index]

    // If filter doesn't exist at this index, add it as a new filter
    if (!viewFilter && filterInfo) {
      this.addFilter(filterInfo.title, { type: filterInfo.type, value: '', title: '' } as BvViewFilterOption, value)
      return
    }

    // If filter exists, update its value
    if (viewFilter) {
      viewFilter.value = value
      this.setFilters(filters)
    }
  }

  addFilter(filter: string, option: BvViewFilterOption, value: string[]) {
    this.setFilters(this.get().selectedFilters.concat(new BvViewFilter(filter, option.type, value)))
  }

  concatFilter(viewFilter: BvViewFilter) {
    this.setFilters(this.get().selectedFilters.concat(viewFilter))
  }

  updateFilter(viewFilter: BvViewFilter, value: string[]) {
    const filters = this.get().selectedFilters.map((f) => {
      if (f.title == viewFilter.title) {
        f.value = value
        return f
      }
      return f
    })
    this.setFilters(filters)
  }

  removeFilter(index: number) {
    const filter = this.get().selectedFilters[index]
    if (!filter) {
      return
    }

    this.setFilters(pullAt([index], this.get().selectedFilters))
    this.patchState((state) => {
      const filterOptions = {
        ...state.filterOptionMap,
      }
      filterOptions[filter.title]?.forEach((f) => (f.selected = false))
      return {
        filterOptionMap: filterOptions,
      }
    })
  }

  async onDateFilterToggle(checked: boolean, filter: string, option: BvViewFilterOption) {
    const existingFilter = this.get().selectedFilters.find((f) => f.title == filter)
    const index = existingFilter ? this.get().selectedFilters.indexOf(existingFilter) : -1

    // If unchecking and filter exists, remove it
    if (!checked && existingFilter) {
      this.removeFilter(index)
      return
    }

    // If unchecking but filter doesn't exist, nothing to do
    if (!checked) {
      return
    }

    let value: string[] = [option.value]
    if (option.title === 'Custom') {
      const ref = this.matDialog.open(DatePickerModal)
      const range = await firstValueFrom(ref.afterClosed())
      if (!range) {
        return
      }
      value = range.start ? [range.start.toISOString()] : []
      if (range.end) {
        value.push(range.end.toISOString())
      }
    }

    if (!existingFilter) {
      this.addFilter(filter, option, value)
    } else {
      const filters = this.get().selectedFilters
      filters[index].value = value
      this.setFilters(filters)
    }
  }

  async onFilterToggle(filter: string, option: BvViewFilterOption) {
    const checked = option.selected
    this.toggleFilterOption({
      filter,
      checked,
      option,
    })

    if (option.type == PropertyType.DateTimeType) {
      this.onDateFilterToggle(checked, filter, option)
      return
    }

    const existingFilter = this.get().selectedFilters.find((f) => f.title == filter)
    const value = [option.value]
    if (!existingFilter) {
      this.addFilter(filter, option, value)
      return
    }
    const index = this.get().selectedFilters.indexOf(existingFilter)
    if (checked) {
      const val = existingFilter.value.concat(option.value)
      const filters = this.get().selectedFilters
      filters[index].value = val
      this.setFilters(filters)
      return
    }

    existingFilter.value = without(value, existingFilter.value)

    if (existingFilter.value.length) {
      const filters = this.get().selectedFilters
      filters[index] = existingFilter
      this.setFilters(filters)
      return
    }

    this.removeFilter(index)
  }

  dateFilterValueToDateRange(value: string[]): DateRange<Date> | undefined {
    switch (value.length) {
      case 0:
        console.log('trying to update empty date range')
        return undefined
      case 1:
        return new DateRange(moment(value[0]).startOf('d').toDate(), null)
      case 2:
        return new DateRange(moment(value[0]).startOf('d').toDate(), moment(value[1]).endOf('d').toDate())
      default:
        console.log('trying to update date range with more than 2 values')
        return undefined
    }
  }

  primaryColumnOrder = ['DateCreated', 'Asset Type', 'SystemId', 'Address']

  private getPropertyValue(property: PropertyValue): string | number | boolean | undefined {
    switch (property.type) {
      case PropertyType.StringType:
        return property.stringValue as string | undefined
      case PropertyType.DateTimeType:
        return property.dateTimeValue
      case PropertyType.DecimalType:
        return property.decimalValue
      case PropertyType.IntType:
        return property.intValue as number | undefined
      case PropertyType.BooleanType:
        return property.booleanValue as boolean | undefined
      default:
        return undefined
    }
  }

  private makeTableRows(assetRows: AssetRow[], selectedColumns: string[]): AssetTableData {
    const columns = new Map<string, ColumnRef>()

    const rows = assetRows.map((r: AssetRow): AssetTableRow => {
      const { propertyValues } = r
      const assetColumns = groupBy('name', propertyValues)
      const row: AssetTableRow = { assetId: r.assetId, layerId: r.layerId }

      keys(assetColumns).forEach((name) => {
        const column: ColumnRef = {
          name,
          key: name,
          label: name.replace(/([a-z])([A-Z])/, '$1 $2'),
          visible: true,
          source: 'asset',
          category: ViewPropertyCategory.AssetDetails,
        }
        columns.set(name, column)
        const group = assetColumns[name]
        const type = first(group)?.type
        if (type) {
          row[name] = {
            propertyKey: name,
            propertyName: name,
            type,
            value: group.map((pv) => this.getPropertyValue(pv)).join(', '),
            source: 'asset',
            format: undefined,
          }
        }
      })

      return row
    })

    const names = columns.keys()
    const columnOrder = selectedColumns ?? this.primaryColumnOrder
    const allColumnNames = Array.from(names)
    const order = [...columnOrder, ...without(columnOrder, allColumnNames)]
    return {
      rows,
      columns: compact(order.map((name) => columns.get(name))),
    }
  }

  onSelectView(view: AssetView | undefined) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { viewId: view ? view.id : null },
      queryParamsHandling: 'merge',
    })
  }
}
